import type { ApplicationConfig } from '@angular/core';
import { inject, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { appRoutes } from './app.routes';
import { SeoService } from './core/seo.service';
import { provideMelloMessenger } from './messenger/messenger.providers';
import { THEME_OPTIONS, ThemeService } from '@crm/ui';
import { BRAND } from './core/brand';
import type { RuntimeEnvironment } from './core/capability/capability.types';
import { buildCapabilityRoutes } from './core/capability/capability.registry';
import { messengerAuthGuard } from './messenger/messenger-auth.guard';
import { roleGuard } from './core/routing/role.guard';
import { MessengerRuntime } from './messenger/messenger-runtime';
import { provideLeadApi } from './shared/data-access/lead-api/lead-api.providers';
import { provideTelephonyApi } from './shared/data-access/telephony-api/telephony-api.providers';

function buildRouter(env?: RuntimeEnvironment) {
  return env
    ? buildCapabilityRoutes(env, [messengerAuthGuard, roleGuard])
    : appRoutes;
}

export function buildAppConfig(options: {
  runtime?: MessengerRuntime;
  env?: RuntimeEnvironment;
} = {}): ApplicationConfig {
  const providers: ApplicationConfig['providers'] = [
    provideBrowserGlobalErrorListeners(),
    provideRouter(buildRouter(options.env), withComponentInputBinding()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: THEME_OPTIONS,
      useValue: {
        storageKey: 'crm-color-scheme',
        defaultScheme: 'light',
        themeColor: {
          light: BRAND.themeColor,
          dark: BRAND.themeColorDark,
        },
      },
    },
    provideAppInitializer(() => {
      inject(ThemeService).start();
      inject(SeoService).start();
    }),
  ];

  if (options.runtime) {
    providers.push({ provide: MessengerRuntime, useValue: options.runtime });
  }

  providers.push(...provideMelloMessenger(), ...provideLeadApi(), ...provideTelephonyApi());

  return { providers };
}

/**
 * Default application config using the production route projection.
 *
 * Runtime bootstrap can use `buildAppConfig` with a resolved environment.
 */
export const appConfig: ApplicationConfig = buildAppConfig();
